import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import teamsData from "./data/teams.json";
import AuthView from "./components/AuthView";
import WhatsNewModal from "./components/WhatsNewModal";
import TeamPicker from "./components/TeamPicker";
import MatchSetup from "./components/MatchSetup";
import MatchSession from "./components/MatchSession";
import SeasonCenter from "./components/SeasonCenter";
import TeamAdminPanel from "./components/TeamAdminPanel";
import ConfirmDialog from "./components/ConfirmDialog";
import AppUpdatePrompt from "./components/AppUpdatePrompt";
import { getChangelogTooltip } from "./changelog";
import { APP_VERSION } from "./config/appVersion";
import { useAppUpdate } from "./hooks/useAppUpdate";
import { useSupabaseAuth } from "./hooks/useSupabaseAuth";
import { useSupabaseMatches } from "./hooks/useSupabaseMatches";
import { useSupabaseTeams } from "./hooks/useSupabaseTeams";
import { useWhatsNew } from "./hooks/useWhatsNew";
import { exportMatchExcel } from "./lib/exportMatchExcel";
import { isSupabaseConfigured, supabase } from "./lib/supabaseClient";
import {
  APP_STATE_KEY,
  SEASON_STATE_KEY,
  SELECTED_SEASON_KEY,
  SELECTED_TEAM_KEY,
  buildSeasonOptions,
  buildSeasonKpis,
  buildSeasonSummary,
  clearTeamQueryParam,
  emptyCounters,
  eventLabel,
  filterSeasonMatchesByTeam,
  getDefaultSeason,
  getMatchSeason,
  getSeasonStartYear,
  loadSaved,
  loadSeasonMatches,
  normalizeSeason,
  sortPlayersForUI
} from "./lib/appHelpers";

const CHANGELOG_TOOLTIP = getChangelogTooltip(APP_VERSION);

const EXTERNAL_TEAM_ID = "__external_team_file__";
const PENDING_ONLINE_MATCHES_KEY = "matchapp-pending-online-matches";
const slugifyPlayerPart = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "player";

function getTeamFromQuery() {
  try {
    const params = new URLSearchParams(window.location.search);
    const team = (params.get("team") || "").toLowerCase();
    return team || null;
  } catch {
    return null;
  }
}

function getTeamFileFromQuery() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("teamFile") || null;
  } catch {
    return null;
  }
}

function getClubReturnPathFromTeamFile(teamFile) {
  if (!teamFile) return null;

  try {
    const cleanPath = String(teamFile).split("?")[0].split("#")[0];
    const parts = cleanPath.split("/").filter(Boolean);
    if (parts.length >= 2) {
      return `/${parts[0]}/`;
    }
  } catch {}

  return null;
}

function loadPendingOnlineMatches() {
  try {
    return JSON.parse(localStorage.getItem(PENDING_ONLINE_MATCHES_KEY)) || [];
  } catch {
    return [];
  }
}

function savePendingOnlineMatches(matches) {
  try {
    localStorage.setItem(PENDING_ONLINE_MATCHES_KEY, JSON.stringify(matches));
  } catch {}
}

export default function App() {
  const auth = useSupabaseAuth();
  const onlineTeams = useSupabaseTeams(auth.user);
  const appUpdate = useAppUpdate();
  const saved = useMemo(() => loadSaved(), []);
  const whatsNew = useWhatsNew();
  const toastTimeoutRef = useRef(null);

  const teamFileFromQuery = getTeamFileFromQuery();
  const clubReturnPath = getClubReturnPathFromTeamFile(teamFileFromQuery);
  const [externalTeamData, setExternalTeamData] = useState(null);
  const [externalTeamLoading, setExternalTeamLoading] = useState(Boolean(teamFileFromQuery));
  const [selectedSeason, setSelectedSeason] = useState(() => {
    const defaultSeason = getDefaultSeason();
    try {
      const savedSeason = localStorage.getItem(SELECTED_SEASON_KEY);
      if (!savedSeason) return defaultSeason;
      return getSeasonStartYear(savedSeason) < getSeasonStartYear(defaultSeason) ? defaultSeason : savedSeason;
    } catch {
      return defaultSeason;
    }
  });

  const [seasonMatches, setSeasonMatches] = useState(() => loadSeasonMatches());
  const [selectedTeamId, setSelectedTeamId] = useState(() => {
    const externalFile = getTeamFileFromQuery();
    if (externalFile) return EXTERNAL_TEAM_ID;

    const fromQuery = getTeamFromQuery();
    if (fromQuery) return fromQuery;
    try {
      return localStorage.getItem(SELECTED_TEAM_KEY) || null;
    } catch {
      return null;
    }
  });

  const [cupEnabled, setCupEnabled] = useState(() => saved?.cupEnabled || false);
  const [cupName, setCupName] = useState(() => saved?.cupName || "");
  const [cupPhase, setCupPhase] = useState(() => saved?.cupPhase || "");
  const [cupPanelOpen, setCupPanelOpen] = useState(
    () => saved?.cupPanelOpen ?? (saved?.cupEnabled ?? false)
  );
  const [selectedPlayers, setSelectedPlayers] = useState(() => saved?.selectedPlayers || []);
  const [step, setStep] = useState(() => saved?.step || 1);
  const [stats, setStats] = useState(() => saved?.stats || {});
  const [history, setHistory] = useState(() => {
    const items = saved?.history || [];
    return items.map((item) => {
      if (item && item.id) return item;
      return {
        ...(item || {}),
        id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        time: Date.now()
      };
    });
  });
  const [matchInfo, setMatchInfo] = useState(
    () => saved?.matchInfo || { date: "", opponent: "", location: "" }
  );
  const [currentHalf, setCurrentHalf] = useState(() => saved?.currentHalf || 1);
  const [viewMode, setViewMode] = useState(() => saved?.viewMode || "match");
  const [seasonOpen, setSeasonOpen] = useState(false);
  const [teamAdminOpen, setTeamAdminOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [pendingOnlineMatches, setPendingOnlineMatches] = useState(() => loadPendingOnlineMatches());

  const availableTeams = useMemo(() => {
    if (isSupabaseConfigured && auth.user) return onlineTeams.teams;
    return teamsData;
  }, [auth.user, onlineTeams.teams]);

  const selectedTeam = useMemo(() => {
    if (selectedTeamId === EXTERNAL_TEAM_ID) return externalTeamData;
    return availableTeams.find((team) => team.id === selectedTeamId) || null;
  }, [availableTeams, selectedTeamId, externalTeamData]);
  const canManageSelectedTeam =
    !selectedTeam?.onlineId || ["owner", "admin"].includes(selectedTeam?.membershipRole);
  const onlineMatches = useSupabaseMatches(auth.user, selectedTeam);

  const basePlayers = useMemo(() => {
    if (selectedTeam && Array.isArray(selectedTeam.players) && selectedTeam.players.length > 0) {
      return selectedTeam.players;
    }
    return [];
  }, [selectedTeam]);

  const allPlayers = useMemo(() => {
    const normalize = (player, index = 0) => {
      const nr = typeof player.nr === "number" ? player.nr : Number(player.nr);
      const shirtNumber =
        typeof player.shirtNumber === "number" ? player.shirtNumber : Number(player.shirtNumber ?? player.nr);
      const name = String(player.name || "").trim();
      return {
        id: player.id || `extra-${slugifyPlayerPart(name)}-${Number.isFinite(shirtNumber) ? shirtNumber : index + 1}`,
        nr,
        shirtNumber,
        name,
        role: player.role === "goalkeeper" ? "goalkeeper" : undefined
      };
    };
    return basePlayers.map((player, index) => normalize(player, index));
  }, [basePlayers]);

  const getPlayerId = useCallback((player) => player?.id ?? player?.nr, []);

  const getPlayerShirtNumber = useCallback((player) => player?.shirtNumber ?? player?.nr, []);

  const playerMatchesRef = useCallback(
    (player, ref) => String(getPlayerId(player)) === String(ref) || String(player?.nr) === String(ref),
    [getPlayerId]
  );

  const findPlayerByRef = useCallback(
    (ref) => allPlayers.find((player) => playerMatchesRef(player, ref)),
    [allPlayers, playerMatchesRef]
  );

  const playersForUI = useMemo(() => sortPlayersForUI(allPlayers), [allPlayers]);
  const pendingMatchesForSelectedTeam = useMemo(
    () =>
      pendingOnlineMatches.filter(
        (match) => match.teamId === selectedTeamId && getMatchSeason(match) === selectedSeason
      ),
    [pendingOnlineMatches, selectedSeason, selectedTeamId]
  );
  const seasonMatchesForView = useMemo(
    () =>
      onlineMatches.online
        ? filterSeasonMatchesByTeam(
            [...onlineMatches.matches, ...pendingMatchesForSelectedTeam],
            selectedTeamId,
            selectedSeason
          )
        : filterSeasonMatchesByTeam(seasonMatches, selectedTeamId, selectedSeason),
    [
      onlineMatches.matches,
      onlineMatches.online,
      pendingMatchesForSelectedTeam,
      seasonMatches,
      selectedSeason,
      selectedTeamId
    ]
  );
  const matchesForPlayerImport = useMemo(
    () =>
      onlineMatches.online
        ? [...onlineMatches.matches, ...pendingOnlineMatches.filter((match) => match.teamId === selectedTeamId)]
        : filterSeasonMatchesByTeam(seasonMatches, selectedTeamId),
    [onlineMatches.matches, onlineMatches.online, pendingOnlineMatches, seasonMatches, selectedTeamId]
  );
  const seasonOptions = useMemo(() => buildSeasonOptions(selectedSeason), [selectedSeason]);

  const seasonSummary = useMemo(
    () => buildSeasonSummary(seasonMatchesForView, selectedTeam ? [selectedTeam] : teamsData),
    [seasonMatchesForView, selectedTeam]
  );
  const seasonKpis = useMemo(
    () => buildSeasonKpis(seasonMatchesForView, seasonSummary.fieldPlayers),
    [seasonMatchesForView, seasonSummary.fieldPlayers]
  );

  const cupLabel = useMemo(() => {
    const active = (cupEnabled || cupPanelOpen) && (cupName || "").trim();
    return active ? `${cupName.trim()}${cupPhase ? ` (${cupPhase})` : ""}` : "";
  }, [cupEnabled, cupPanelOpen, cupName, cupPhase]);

  const showToast = useCallback((text) => {
    setToast({ text });
    window.clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = window.setTimeout(() => setToast(null), 2000);
  }, []);

  const requestConfirm = useCallback((options) => {
    setConfirmDialog(options);
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog(null);
  }, []);

  useEffect(() => {
    try {
      if (selectedTeamId && selectedTeamId !== EXTERNAL_TEAM_ID) {
        localStorage.setItem(SELECTED_TEAM_KEY, selectedTeamId);
      }
    } catch {}
  }, [selectedTeamId]);

  useEffect(() => {
    try {
      localStorage.setItem(SELECTED_SEASON_KEY, selectedSeason);
    } catch {}
  }, [selectedSeason]);

  useEffect(() => {
    if (!teamFileFromQuery) {
      setExternalTeamData(null);
      setExternalTeamLoading(false);
      return;
    }

    let cancelled = false;

    const loadExternalTeam = async () => {
      try {
        setExternalTeamLoading(true);
        const response = await fetch(teamFileFromQuery);
        if (!response.ok) {
          throw new Error(`Kunde inte läsa lagfilen: ${teamFileFromQuery}`);
        }

        const data = await response.json();
        const mappedPlayers = Array.isArray(data?.players)
          ? data.players.map((player, index) => {
              const shirtNumber = Number(player.number);
              const name = String(player.name || "").trim();
              return {
                id:
                  player.id ||
                  `${EXTERNAL_TEAM_ID}-${slugifyPlayerPart(name)}-${Number.isFinite(shirtNumber) ? shirtNumber : index + 1}`,
                nr: shirtNumber,
                shirtNumber,
                name,
                role: player.type === "goalkeeper" ? "goalkeeper" : undefined
              };
            })
          : [];

        if (!cancelled) {
          setExternalTeamData({
            id: EXTERNAL_TEAM_ID,
            name: data?.teamName || "Externt lag",
            players: mappedPlayers
          });
          setSelectedTeamId(EXTERNAL_TEAM_ID);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setExternalTeamData({
            id: EXTERNAL_TEAM_ID,
            name: "Externt lag",
            players: []
          });
          setSelectedTeamId(EXTERNAL_TEAM_ID);
        }
      } finally {
        if (!cancelled) {
          setExternalTeamLoading(false);
        }
      }
    };

    loadExternalTeam();

    return () => {
      cancelled = true;
    };
  }, [teamFileFromQuery]);

  useEffect(() => {
    try {
      localStorage.setItem(SEASON_STATE_KEY, JSON.stringify(seasonMatches));
    } catch {}
  }, [seasonMatches]);

  useEffect(() => {
    savePendingOnlineMatches(pendingOnlineMatches);
  }, [pendingOnlineMatches]);

  useEffect(() => {
    if (!onlineMatches.online || pendingMatchesForSelectedTeam.length === 0) return undefined;

    let cancelled = false;

    const syncPendingMatches = async () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;

      for (const pendingMatch of pendingMatchesForSelectedTeam) {
        if (cancelled) return;

        const { error } = await onlineMatches.saveMatch(pendingMatch);
        if (error) return;

        setPendingOnlineMatches((prev) => prev.filter((match) => match.id !== pendingMatch.id));
      }
    };

    syncPendingMatches();
    window.addEventListener("online", syncPendingMatches);

    return () => {
      cancelled = true;
      window.removeEventListener("online", syncPendingMatches);
    };
  }, [onlineMatches, pendingMatchesForSelectedTeam]);

  const persist = useCallback(() => {
    localStorage.setItem(
      APP_STATE_KEY,
      JSON.stringify({
        selectedPlayers,
        step,
        stats,
        matchInfo,
        history,
        currentHalf,
        viewMode,
        cupEnabled,
        cupName,
        cupPhase,
        cupPanelOpen
      })
    );
  }, [
    selectedPlayers,
    step,
    stats,
    matchInfo,
    history,
    currentHalf,
    viewMode,
    cupEnabled,
    cupName,
    cupPhase,
    cupPanelOpen
  ]);

  useEffect(() => {
    persist();
  }, [persist]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (step === 2 && history.length > 0) {
        persist();
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [step, history.length, persist]);

  useEffect(() => {
    return () => window.clearTimeout(toastTimeoutRef.current);
  }, []);

  const ensurePlayerStats = useCallback((nr) => {
    setStats((prev) => {
      const current = prev[nr];
      if (current && current.byHalf) return prev;
      const base = current || emptyCounters();
      return {
        ...prev,
        [nr]: {
          ...emptyCounters(),
          ...base,
          byHalf: {
            1: { ...(base.byHalf?.[1] || emptyCounters()) },
            2: { ...(base.byHalf?.[2] || emptyCounters()) }
          }
        }
      };
    });
  }, []);

  const togglePlayer = useCallback((playerId) => {
    setSelectedPlayers((prev) =>
      prev.includes(playerId)
        ? prev.filter((item) => item !== playerId)
        : [...prev, playerId]
    );
  }, []);

  const handleMatchInfoChange = useCallback((event) => {
    const { name, value } = event.target;
    setMatchInfo((prev) => ({ ...prev, [name]: value }));
  }, []);

  const startMatch = useCallback(() => {
    if (!matchInfo?.date || !matchInfo?.opponent || !matchInfo?.location) {
      alert("Fyll i datum, motståndare och Hemma/Borta innan du startar matchen.");
      return;
    }

    if (selectedPlayers.length === 0) {
      alert("Välj minst en spelare");
      return;
    }

    const initialStats = {};
    selectedPlayers.forEach((nr) => {
      initialStats[nr] = {
        ...emptyCounters(),
        byHalf: { 1: emptyCounters(), 2: emptyCounters() }
      };
    });
    setStats(initialStats);
    setCurrentHalf(1);
    setStep(2);
    setViewMode("match");
  }, [matchInfo, selectedPlayers]);

  const increment = useCallback(
    (nr, type) => {
      ensurePlayerStats(nr);

      const player = findPlayerByRef(nr);
      const isGoalkeeper = player?.role === "goalkeeper";

      let alsoType = null;
      if (isGoalkeeper && type === "sevenGoal") alsoType = "goal";
      if (isGoalkeeper && type === "sevenMiss") alsoType = "save";

      setStats((prev) => {
        const playerStats = prev[nr] || {
          ...emptyCounters(),
          byHalf: { 1: emptyCounters(), 2: emptyCounters() }
        };
        const byHalf = playerStats.byHalf || { 1: emptyCounters(), 2: emptyCounters() };
        const incOne = (obj, key) => ({ ...obj, [key]: (obj[key] || 0) + 1 });

        let next = {
          ...playerStats,
          ...incOne(playerStats, type),
          byHalf: {
            ...byHalf,
            [currentHalf]: incOne(byHalf[currentHalf] || {}, type)
          }
        };

        if (alsoType) {
          next = {
            ...next,
            ...incOne(next, alsoType),
            byHalf: {
              ...next.byHalf,
              [currentHalf]: incOne(next.byHalf[currentHalf] || {}, alsoType)
            }
          };
        }

        return { ...prev, [nr]: next };
      });

      setHistory((prev) => [
        ...prev,
        {
          id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
          time: Date.now(),
          playerId: getPlayerId(player),
          nr: getPlayerShirtNumber(player),
          type,
          alsoType,
          half: currentHalf
        }
      ]);

      showToast(`#${getPlayerShirtNumber(player) ?? nr} ${player?.name || ""} – ${eventLabel(type, player)}`);
    },
    [currentHalf, ensurePlayerStats, findPlayerByRef, getPlayerId, getPlayerShirtNumber, showToast]
  );

  const undoLast = useCallback(() => {
    if (history.length === 0) return;
    const last = history[history.length - 1];

    setStats((prev) => {
      const playerRef = last.playerId ?? last.nr;
      const playerStats = prev[playerRef];
      if (!playerStats) return prev;

      const byHalf = { ...(playerStats.byHalf || { 1: emptyCounters(), 2: emptyCounters() }) };
      const half = last.half || currentHalf;
      const dec = (obj, key) => Math.max(0, (obj[key] || 0) - 1);

      let next = {
        ...playerStats,
        [last.type]: dec(playerStats, last.type),
        byHalf: {
          ...byHalf,
          [half]: {
            ...(byHalf[half] || {}),
            [last.type]: dec(byHalf[half] || {}, last.type)
          }
        }
      };

      if (last.alsoType) {
        next = {
          ...next,
          [last.alsoType]: dec(next, last.alsoType),
          byHalf: {
            ...next.byHalf,
            [half]: {
              ...(next.byHalf[half] || {}),
              [last.alsoType]: dec(next.byHalf[half] || {}, last.alsoType)
            }
          }
        };
      }

      return { ...prev, [playerRef]: next };
    });

    setHistory((prev) => prev.slice(0, -1));
    const player = findPlayerByRef(last.playerId ?? last.nr);
    showToast(
      `Ångrade: #${last.nr ?? getPlayerShirtNumber(player) ?? ""} ${player?.name || ""} – ${eventLabel(last.type, player)}`
    );
  }, [currentHalf, findPlayerByRef, getPlayerShirtNumber, history, showToast]);

  const deleteHistoryItem = useCallback(
    (id) => {
      const item = history.find((entry) => entry.id === id);
      if (!item) return;

      setStats((prev) => {
        const playerRef = item.playerId ?? item.nr;
        const playerStats = prev[playerRef];
        if (!playerStats) return prev;

        const byHalf = { ...(playerStats.byHalf || { 1: emptyCounters(), 2: emptyCounters() }) };
        const half = item.half || currentHalf;
        const dec = (obj, key) => Math.max(0, (obj[key] || 0) - 1);

        let next = {
          ...playerStats,
          [item.type]: dec(playerStats, item.type),
          byHalf: {
            ...byHalf,
            [half]: {
              ...(byHalf[half] || {}),
              [item.type]: dec(byHalf[half] || {}, item.type)
            }
          }
        };

        if (item.alsoType) {
          next = {
            ...next,
            [item.alsoType]: dec(next, item.alsoType),
            byHalf: {
              ...next.byHalf,
              [half]: {
                ...(next.byHalf[half] || {}),
                [item.alsoType]: dec(next.byHalf[half] || {}, item.alsoType)
              }
            }
          };
        }

        return { ...prev, [playerRef]: next };
      });

      setHistory((prev) => prev.filter((entry) => entry.id !== id));
      const player = findPlayerByRef(item.playerId ?? item.nr);
      showToast(
        `Raderade: #${item.nr ?? getPlayerShirtNumber(player) ?? ""} ${player?.name || ""} – ${eventLabel(item.type, player)}`
      );
    },
    [currentHalf, findPlayerByRef, getPlayerShirtNumber, history, showToast]
  );

  const computeFinalScore = useCallback(
    (statsObj) => {
      let ourGoals = 0;
      let oppGoals = 0;

      Object.entries(statsObj || {}).forEach(([nr, playerStats]) => {
        const player = findPlayerByRef(nr);
        if (!player) return;

        if (player.role === "goalkeeper") {
          oppGoals += playerStats.goal || 0;
          ourGoals += playerStats.gkScored || 0;
        } else {
          ourGoals += (playerStats.goal || 0) + (playerStats.sevenGoal || 0);
        }
      });

      const isHomeMatch = (matchInfo?.location || "") === "Hemma";
      return {
        home: isHomeMatch ? ourGoals : oppGoals,
        away: isHomeMatch ? oppGoals : ourGoals
      };
    },
    [findPlayerByRef, matchInfo?.location]
  );

  const saveCurrentMatchToSeason = useCallback(async () => {
    const result = computeFinalScore(stats);
    const matchRecord = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      createdAt: new Date().toISOString(),
      teamId: selectedTeamId,
      teamName: selectedTeam?.name || "",
      season: selectedSeason,
      matchInfo: { ...matchInfo, season: selectedSeason },
      matchType: cupEnabled || cupPanelOpen ? "cup" : "series",
      cupName: cupEnabled || cupPanelOpen ? (cupName || "").trim() : "",
      cupPhase: cupEnabled || cupPanelOpen ? (cupPhase || "").trim() : "",
      result,
      selectedPlayers: [...selectedPlayers],
      playerRoster: [...selectedPlayers]
        .map((playerRef) => {
          const player = findPlayerByRef(playerRef);
          const playerId = getPlayerId(player) ?? playerRef;
          const shirtNumber = getPlayerShirtNumber(player);
          return {
            id: playerId,
            playerId,
            nr: Number(shirtNumber),
            shirtNumber: Number(shirtNumber),
            name: player?.name || "",
            role: player?.role === "goalkeeper" ? "goalkeeper" : undefined
          };
        })
        .filter((player) => Number.isFinite(Number(player.shirtNumber))),
      stats: JSON.parse(JSON.stringify(stats || {})),
      history: JSON.parse(JSON.stringify(history || []))
    };

    const alreadySaved = seasonMatchesForView.some((match) => {
      return (
        match.teamId === matchRecord.teamId &&
        match.matchInfo?.date === matchRecord.matchInfo?.date &&
        match.matchInfo?.opponent === matchRecord.matchInfo?.opponent &&
        match.matchInfo?.location === matchRecord.matchInfo?.location &&
        JSON.stringify(match.result || {}) === JSON.stringify(matchRecord.result || {})
      );
    });

    if (alreadySaved) {
      showToast("Matchen finns redan sparad i säsongen");
      return;
    }

    if (onlineMatches.online) {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setPendingOnlineMatches((prev) => [...prev, { ...matchRecord, pendingSync: true }]);
        showToast("Match sparad offline och synkas när nätet är tillbaka");
        return;
      }

      const { error } = await onlineMatches.saveMatch(matchRecord);
      if (error) {
        setPendingOnlineMatches((prev) => [...prev, { ...matchRecord, pendingSync: true }]);
        showToast("Match sparad offline och synkas senare");
        return;
      }

      showToast("Match sparad online");
      return;
    }

    setSeasonMatches((prev) => {
      showToast("Match sparad i säsongen");
      return [...prev, matchRecord];
    });
  }, [
    cupEnabled,
    cupName,
    cupPanelOpen,
    cupPhase,
    findPlayerByRef,
    getPlayerId,
    getPlayerShirtNumber,
    computeFinalScore,
    history,
    matchInfo,
    onlineMatches,
    selectedPlayers,
    selectedSeason,
    selectedTeam,
    selectedTeamId,
    seasonMatchesForView,
    showToast,
    stats
  ]);

  const resetMatchState = useCallback(() => {
    setStats({});
    setHistory([]);
    setSelectedPlayers([]);
    setMatchInfo({ date: "", opponent: "", location: "" });
    setCurrentHalf(1);
    setViewMode("match");
    setStep(1);

    localStorage.setItem(
      APP_STATE_KEY,
      JSON.stringify({
        selectedPlayers: [],
        step: 1,
        stats: {},
        matchInfo: { date: "", opponent: "", location: "" },
        history: [],
        currentHalf: 1,
        viewMode: "match",
        cupEnabled,
        cupName,
        cupPhase,
        cupPanelOpen
      })
    );

    window.scrollTo(0, 0);
  }, [
    cupEnabled,
    cupName,
    cupPanelOpen,
    cupPhase
  ]);

  const confirmReset = useCallback(() => {
    if (step === 2 && (history.length > 0 || Object.keys(stats || {}).length > 0)) {
      requestConfirm({
        title: "Avsluta match?",
        message: `Spara matchen (${matchInfo?.date || "-"} vs ${matchInfo?.opponent || "-"}) i säsongen innan du startar ny match?`,
        confirmText: "Spara och avsluta",
        secondaryText: "Avsluta utan att spara",
        cancelText: "Avbryt",
        variant: "danger",
        onConfirm: async () => {
          await saveCurrentMatchToSeason();
          resetMatchState();
        },
        onSecondary: () => {
          requestConfirm({
            title: "Avsluta utan att spara?",
            message: "Matchen tas bort från pågående läge och sparas inte i säsongen.",
            confirmText: "Ja, avsluta utan att spara",
            cancelText: "Avbryt",
            variant: "danger",
            onConfirm: resetMatchState
          });
        }
      });
      return;
    }

    resetMatchState();
  }, [
    history.length,
    matchInfo,
    requestConfirm,
    resetMatchState,
    saveCurrentMatchToSeason,
    stats,
    step
  ]);

  const exportSeasonJson = useCallback(() => {
    const data = {
      exportedAt: new Date().toISOString(),
      teamId: selectedTeamId || null,
      teamName: selectedTeam?.name || null,
      season: selectedSeason,
      matches: seasonMatchesForView
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const teamPart =
      (selectedTeam?.name || selectedTeamId || "season")
        .toString()
        .replace(/[^\p{L}\p{N}\-_ ]/gu, "")
        .trim() || "season";
    link.href = url;
    link.download = `matchapp_season_${teamPart}_${selectedSeason.replace("/", "-")}_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast("Säsong (JSON) nedladdad");
  }, [seasonMatchesForView, selectedSeason, selectedTeam, selectedTeamId, showToast]);

  const downloadExcel = useCallback(async (savedMatch = null) => {
    if (savedMatch) {
      const roster = Array.isArray(savedMatch.playerRoster)
        ? savedMatch.playerRoster.map((player) => ({
            ...player,
            nr: player.nr ?? player.shirtNumber,
            shirtNumber: player.shirtNumber ?? player.nr,
            id: player.playerId ?? player.id,
            playerId: player.playerId ?? player.id
          }))
        : [];

      await exportMatchExcel({
        matchInfo: savedMatch.matchInfo || {},
        cupEnabled: savedMatch.matchType === "cup",
        cupPanelOpen: savedMatch.matchType === "cup",
        cupName: savedMatch.cupName || "",
        cupPhase: savedMatch.cupPhase || "",
        allPlayers: roster,
        selectedPlayers: roster.map((player) => player.playerId ?? player.id ?? player.nr),
        stats: savedMatch.stats || {}
      });
      return;
    }

    await exportMatchExcel({
      matchInfo,
      cupEnabled,
      cupPanelOpen,
      cupName,
      cupPhase,
      allPlayers,
      selectedPlayers,
      stats
    });
  }, [allPlayers, cupEnabled, cupName, cupPanelOpen, cupPhase, matchInfo, selectedPlayers, stats]);

  const handleDeleteSeasonMatch = useCallback(
    async (matchId) => {
      if (!canManageSelectedTeam) {
        showToast("Endast ägare/admin kan ta bort matcher");
        return;
      }

    if (onlineMatches.online) {
      const pendingMatch = pendingOnlineMatches.find((match) => match.id === matchId);
      if (pendingMatch) {
        setPendingOnlineMatches((prev) => prev.filter((match) => match.id !== matchId));
        showToast("Match borttagen");
        return;
      }

      const { error } = await onlineMatches.deleteMatch(matchId);
        if (error) {
          showToast(`Kunde inte ta bort online: ${error.message}`);
          return;
        }

        showToast("Match borttagen");
        return;
      }

      setSeasonMatches((prev) => prev.filter((match) => match.id !== matchId));
      showToast("Match borttagen");
    },
    [canManageSelectedTeam, onlineMatches, pendingOnlineMatches, showToast]
  );

  const handleClearSeason = useCallback(async () => {
    if (!canManageSelectedTeam) {
      showToast("Endast ägare/admin kan rensa säsongen");
      return;
    }

    if (seasonMatchesForView.length === 0) {
      showToast("Säsongen är redan tom");
      return;
    }

    if (onlineMatches.online) {
      setPendingOnlineMatches((prev) =>
        prev.filter((match) => !(match.teamId === selectedTeamId && getMatchSeason(match) === selectedSeason))
      );

      for (const match of seasonMatchesForView) {
        if (match.pendingSync) continue;
        const { error } = await onlineMatches.deleteMatch(match.id);
        if (error) {
          showToast(`Kunde inte rensa online: ${error.message}`);
          return;
        }
      }

      showToast("Säsongen rensad");
      setSeasonOpen(false);
      return;
    }

    setSeasonMatches((prev) =>
      prev.filter(
        (match) => !((selectedTeamId ? match.teamId === selectedTeamId : true) && getMatchSeason(match) === selectedSeason)
      )
    );

    showToast("Säsongen rensad");
    setSeasonOpen(false);
  }, [
    canManageSelectedTeam,
    onlineMatches,
    seasonMatchesForView,
    selectedSeason,
    selectedTeamId,
    showToast
  ]);

  const getSeasonMatchKey = useCallback((match) => {
    const result = match?.result || {};
    return [
      match?.teamId || selectedTeamId || "",
      getMatchSeason(match) || "",
      match?.matchInfo?.date || "",
      match?.matchInfo?.opponent || "",
      match?.matchInfo?.location || "",
      result.home ?? "",
      result.away ?? ""
    ].join("|");
  }, [selectedTeamId]);

  const handleImportSeasonBackup = useCallback(
    async (data) => {
      if (data?.invalid || !Array.isArray(data?.matches)) {
        showToast("Kunde inte läsa backupfilen");
        return;
      }

      if (!selectedTeamId || !selectedTeam) {
        showToast("Välj lag innan import");
        return;
      }

      const importedMatches = data.matches
        .filter((match) => !data.teamId || match.teamId === data.teamId)
        .map((match) => {
          const season = normalizeSeason(match.season || match.matchInfo?.season || getMatchSeason(match) || selectedSeason);
          return {
            ...match,
            id: match.id || `${Date.now()}_${Math.random().toString(36).slice(2)}`,
            createdAt: match.createdAt || new Date().toISOString(),
            teamId: selectedTeamId,
            teamName: selectedTeam.name || match.teamName || "",
            matchInfo: { ...(match.matchInfo || {}), season },
            season,
            matchType: match.matchType || "series",
            cupName: match.cupName || "",
            cupPhase: match.cupPhase || "",
            result: match.result || {},
            selectedPlayers: match.selectedPlayers || [],
            playerRoster: match.playerRoster || [],
            stats: match.stats || {},
            history: match.history || []
          };
        });

      if (importedMatches.length === 0) {
        showToast("Backupen innehåller inga matcher för valt lag");
        return;
      }

      const existingMatchesForTeam = onlineMatches.online
        ? [
            ...onlineMatches.matches,
            ...pendingOnlineMatches.filter((match) => match.teamId === selectedTeamId)
          ]
        : filterSeasonMatchesByTeam(seasonMatches, selectedTeamId);
      const existingKeys = new Set(existingMatchesForTeam.map(getSeasonMatchKey));
      const matchesToImport = [];
      let skipped = 0;

      importedMatches.forEach((match) => {
        const key = getSeasonMatchKey(match);
        if (existingKeys.has(key)) {
          skipped += 1;
          return;
        }
        existingKeys.add(key);
        matchesToImport.push(match);
      });

      if (matchesToImport.length === 0) {
        showToast(`Inga nya matcher att importera (${skipped} fanns redan)`);
        return;
      }

      if (onlineMatches.online) {
        let imported = 0;

        for (const match of matchesToImport) {
          const { error } = await onlineMatches.saveMatch(match);
          if (error) {
            showToast(`Import avbruten: ${error.message}`);
            return;
          }
          imported += 1;
        }

        showToast(`Importerade ${imported} matcher online${skipped ? `, hoppade över ${skipped}` : ""}`);
        return;
      }

      setSeasonMatches((prev) => [...prev, ...matchesToImport]);
      showToast(`Importerade ${matchesToImport.length} matcher${skipped ? `, hoppade över ${skipped}` : ""}`);
    },
    [
      getSeasonMatchKey,
      onlineMatches,
      pendingOnlineMatches,
      seasonMatches,
      selectedTeam,
      selectedTeamId,
      selectedSeason,
      showToast
    ]
  );

  const handleChangeTeam = useCallback(() => {
    requestConfirm({
      title: "Byta lag?",
      message: "All pågående matchdata raderas när du byter lag.",
      confirmText: "Byt lag",
      cancelText: "Avbryt",
      variant: "danger",
      onConfirm: () => {
        closeConfirm();
        localStorage.removeItem(SELECTED_TEAM_KEY);
        setExternalTeamData(null);

        if (teamFileFromQuery && clubReturnPath) {
          window.location.href = clubReturnPath;
          return;
        }

        clearTeamQueryParam();
        window.location.reload();
      }
    });
  }, [closeConfirm, clubReturnPath, requestConfirm, teamFileFromQuery]);

  const handleSignOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
  }, []);

  const handleTeamCreated = useCallback(
    (createdTeam) => {
      if (!createdTeam) return;

      const nextTeam = {
        id: createdTeam.slug || createdTeam.id,
        onlineId: createdTeam.id,
        name: createdTeam.name,
        membershipRole: "owner",
        players: []
      };

      onlineTeams.setTeams((prev) => [...prev, nextTeam]);
      setSelectedTeamId(nextTeam.id);
      showToast("Lag skapat");
    },
    [onlineTeams, showToast]
  );

  const handleTeamPlayersChanged = useCallback(
    (players) => {
      if (!selectedTeam?.id) return;

      const mappedPlayers = (players || [])
        .filter((player) => player.active !== false)
        .map((player) => ({
          id: player.id,
          nr: Number(player.shirt_number),
          shirtNumber: Number(player.shirt_number),
          name: player.name,
          role: player.role === "goalkeeper" ? "goalkeeper" : undefined
        }));

      onlineTeams.setTeams((prev) =>
        prev.map((team) =>
          team.id === selectedTeam.id
            ? { ...team, players: mappedPlayers }
            : team
        )
      );
    },
    [onlineTeams, selectedTeam]
  );

  const canStartMatch =
    Boolean(matchInfo?.date) && Boolean(matchInfo?.opponent) && Boolean(matchInfo?.location);

  const liveScore = useMemo(() => {
    let our = 0;
    let opp = 0;

    Object.entries(stats).forEach(([nr, playerStats]) => {
      const player = findPlayerByRef(nr);
      if (!player) return;

      if (player.role === "goalkeeper") {
        opp += playerStats.goal || 0;
        our += playerStats.gkScored || 0;
      } else {
        our += (playerStats.goal || 0) + (playerStats.sevenGoal || 0);
      }
    });

    return { our, opp };
  }, [findPlayerByRef, stats]);

  const isHome = (matchInfo?.location || "") === "Hemma";
  const topbarLiveHome = isHome ? liveScore.our : liveScore.opp;
  const topbarLiveAway = isHome ? liveScore.opp : liveScore.our;
  const onlineStatus = onlineMatches.online
    ? pendingMatchesForSelectedTeam.length > 0
      ? `${pendingMatchesForSelectedTeam.length} match${pendingMatchesForSelectedTeam.length === 1 ? "" : "er"} väntar på synk`
      : onlineTeams.usingCache
        ? "Offline: cachelagrat lag"
        : onlineMatches.loading
        ? "Synkar..."
        : "Synkad online"
    : "Lokal lagring";
  const updatePrompt = (
    <AppUpdatePrompt
      visible={appUpdate.updateAvailable}
      onReload={appUpdate.reloadToUpdate}
      reloading={appUpdate.reloading}
    />
  );

  if (externalTeamLoading) {
    return (
      <div className="p-6 max-w-md mx-auto">
        <h2 className="text-xl font-semibold mb-4">Laddar lag...</h2>
        {updatePrompt}
      </div>
    );
  }

  if (isSupabaseConfigured && auth.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 shadow-sm">
          Laddar inloggning...
        </div>
        {updatePrompt}
      </div>
    );
  }

  if (isSupabaseConfigured && !auth.user) {
    return (
      <>
        <AuthView appVersion={APP_VERSION} />
        {updatePrompt}
      </>
    );
  }

  if (isSupabaseConfigured && onlineTeams.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 shadow-sm">
          Hämtar dina lag...
        </div>
        {updatePrompt}
      </div>
    );
  }

  if (!selectedTeamId) {
    return (
      <>
        <TeamPicker
          teams={availableTeams}
          appVersion={APP_VERSION}
          onSelectTeam={setSelectedTeamId}
          error={onlineTeams.error}
          onTeamCreated={isSupabaseConfigured && auth.user ? handleTeamCreated : null}
        />
        {updatePrompt}
      </>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto relative">
      {isSupabaseConfigured && auth.user && !(step === 2 && selectedPlayers.length > 0) && (
        <div className="mb-3 flex justify-end">
          <div className="w-full rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 shadow-sm sm:w-auto">
            <div className="mb-1 flex min-w-0 items-center justify-end gap-2 text-xs text-slate-500">
              <span className="min-w-0 truncate">{auth.user.email}</span>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                  pendingMatchesForSelectedTeam.length > 0
                    ? "bg-amber-100 text-amber-800"
                    : onlineTeams.usingCache
                      ? "bg-amber-100 text-amber-800"
                    : onlineMatches.online
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-slate-100 text-slate-700"
                }`}
              >
                {onlineStatus}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
              <button
                type="button"
                onClick={() => setSeasonOpen(true)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Säsong
              </button>
              {selectedTeam?.onlineId && (
                <button
                  type="button"
                  onClick={() => setTeamAdminOpen(true)}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Lagadmin
                </button>
              )}
              <button
                type="button"
                onClick={handleChangeTeam}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Byt lag
              </button>
              <button
                type="button"
                onClick={handleSignOut}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-800"
              >
                Logga ut
              </button>
            </div>
          </div>
        </div>
      )}

      {(step === 1 || (step === 2 && selectedPlayers.length === 0)) && (
        <MatchSetup
          teamName={selectedTeam?.name}
          matchInfo={matchInfo}
          onMatchInfoChange={handleMatchInfoChange}
          playersForUI={playersForUI}
          selectedPlayers={selectedPlayers}
          onTogglePlayer={togglePlayer}
          cupPanelOpen={cupPanelOpen}
          setCupPanelOpen={setCupPanelOpen}
          setCupEnabled={setCupEnabled}
          cupName={cupName}
          setCupName={setCupName}
          cupPhase={cupPhase}
          setCupPhase={setCupPhase}
          onStartMatch={startMatch}
          canStartMatch={canStartMatch}
          appVersion={APP_VERSION}
          changelogTooltip={CHANGELOG_TOOLTIP}
        />
      )}

      {step === 2 && selectedPlayers.length > 0 && (
        <MatchSession
          currentHalf={currentHalf}
          setCurrentHalf={setCurrentHalf}
          viewMode={viewMode}
          setViewMode={setViewMode}
          undoLast={undoLast}
          onReset={confirmReset}
          matchInfo={matchInfo}
          liveHome={topbarLiveHome}
          liveAway={topbarLiveAway}
          cupLabel={cupLabel}
          history={history}
          allPlayers={allPlayers}
          onDeleteHistoryItem={deleteHistoryItem}
          selectedPlayers={selectedPlayers}
          stats={stats}
          increment={increment}
          playersForUI={playersForUI}
          onConfirm={requestConfirm}
        />
      )}

      <SeasonCenter
        open={seasonOpen}
        selectedTeam={selectedTeam}
        selectedSeason={selectedSeason}
        seasonOptions={seasonOptions}
        onSeasonChange={setSelectedSeason}
        seasonKpis={seasonKpis}
        onExportBackup={exportSeasonJson}
        onImportBackup={handleImportSeasonBackup}
        onClose={() => setSeasonOpen(false)}
        seasonSummary={seasonSummary}
        matches={seasonMatchesForView}
        onDeleteMatch={handleDeleteSeasonMatch}
        onClearSeason={handleClearSeason}
        onExportMatchExcel={downloadExcel}
        onConfirm={requestConfirm}
        canManageSeason={canManageSelectedTeam}
      />

      <TeamAdminPanel
        open={teamAdminOpen}
        team={selectedTeam}
        currentUser={auth.user}
        onClose={() => setTeamAdminOpen(false)}
        onToast={showToast}
        onPlayersChanged={handleTeamPlayersChanged}
        onConfirm={requestConfirm}
        matches={matchesForPlayerImport}
      />

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        {...(confirmDialog || {})}
        onConfirm={() => {
          const onConfirm = confirmDialog?.onConfirm;
          closeConfirm();
          onConfirm?.();
        }}
        onSecondary={() => {
          const onSecondary = confirmDialog?.onSecondary;
          closeConfirm();
          onSecondary?.();
        }}
        onCancel={() => {
          const onCancel = confirmDialog?.onCancel;
          closeConfirm();
          onCancel?.();
        }}
      />

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed left-1/2 -translate-x-1/2 bottom-6 z-50 bg-green-600 text-white px-4 py-2 rounded-xl shadow-lg"
        >
          {toast.text}
        </div>
      )}

      <WhatsNewModal
        open={whatsNew.open}
        title={`Vad är nytt i MatchApp ${whatsNew.version}?`}
        items={whatsNew.items}
        onClose={whatsNew.close}
      />

      {updatePrompt}
    </div>
  );
}
